/**
 * Google Analytics 4 utility -- consent-gated.
 * GA only loads after user accepts analytics cookies via CookieConsentBanner.
 */

const GA_MEASUREMENT_ID = 'G-T5LRH57WPC';

let initialized = false;

/**
 * Check if user has consented to analytics cookies.
 */
export function hasAnalyticsConsent() {
  try {
    const consent = JSON.parse(localStorage.getItem('cookie_consent'));
    return consent?.analytics === true;
  } catch {
    return false;
  }
}

/**
 * Load the gtag.js script and initialize GA4.
 * Call after user consents to analytics cookies.
 */
export function initAnalytics() {
  if (initialized || typeof window === 'undefined') return;
  if (!hasAnalyticsConsent()) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We send page views manually for SPA routing
  });

  initialized = true;

  // Consent covers session replay too (Cookie Policy §2.3a).
  import('../instrument').then((m) => m.enableSessionReplay?.()).catch(() => {});
}

/**
 * Track a custom event.
 */
export function trackEvent(eventName, params = {}) {
  if (!initialized || !window.gtag) return;
  window.gtag('event', eventName, params);
}

/**
 * Generalise a route before it is sent to Google Analytics.
 *
 * Google's Analytics terms forbid sending health-related data, and on this
 * platform a path like /questionnaire/Q21 identifies which end-of-life
 * question a member is answering. Dynamic segments on sensitive routes are
 * replaced with placeholders so GA sees the *kind* of page, never which one.
 * Query strings and fragments are never sent.
 */
const PATH_RULES = [
  [/^\/questionnaire\/[^/]+/, '/questionnaire/:question'],
  [/^\/progress\/section\/[^/]+/, '/progress/section/:section'],
  [/^\/team\/[^/]+\/member\/[^/]+/, '/team/:team/member/:member'],
  [/^\/team\/[^/]+\/channels\/[^/]+/, '/team/:team/channels/:channel'],
  [/^\/team\/[^/]+\/channels/, '/team/:team/channels'],
  [/^\/team\/[^/]+\/care-fund\/pool\/[^/]+/, '/team/:team/care-fund/pool/:pool'],
  [/^\/team\/[^/]+\/care-fund/, '/team/:team/care-fund'],
  [/^\/team\/[^/]+\/(billing|get-sponsored)/, '/team/:team/$1'],
  [/^\/team\/[^/]+/, '/team/:team'],
  [/^\/user\/[^/]+/, '/user/:user'],
  [/^\/workbook\/team\/[^/]+\/exercise\/[^/]+\/([^/]+)/, '/workbook/team/:team/exercise/:exercise/$1'],
  [/^\/workbook\/team\/[^/]+\/exercise\/[^/]+/, '/workbook/team/:team/exercise/:exercise'],
  [/^\/workbook\/team\/[^/]+\/contribute\/[^/]+/, '/workbook/team/:team/contribute/:exercise'],
  [/^\/workbook\/team\/[^/]+\/stage\/[^/]+\/summary/, '/workbook/team/:team/stage/:stage/summary'],
  [/^\/workbook\/team\/[^/]+\/stage\/[^/]+/, '/workbook/team/:team/stage/:stage'],
  [/^\/workbook\/team\/[^/]+\/care-pack/, '/workbook/team/:team/care-pack'],
  [/^\/workbook\/team\/[^/]+/, '/workbook/team/:team'],
  [/^\/workbook\/(exercise|stage)\/.+/, '/workbook/$1/:id'],
  [/^\/advocacy-preview\/stage\/[^/]+/, '/advocacy-preview/stage/:stage'],
  [/^\/(sponsor|gift|care-fund\/confirm|sponsor\/credits)\/[^/]+/, '/$1/:token'],
  [/^\/search.*/, '/search'],
];

export function sanitizePathForAnalytics(path) {
  if (typeof path !== 'string') return '/';
  const clean = path.split('?')[0].split('#')[0] || '/';
  for (const [pattern, replacement] of PATH_RULES) {
    if (pattern.test(clean)) return clean.replace(pattern, replacement);
  }
  return clean;
}

/**
 * Track a page view (SPA route change). The path is generalised first —
 * see sanitizePathForAnalytics.
 */
export function trackPageView(path) {
  if (!initialized || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: sanitizePathForAnalytics(path),
    page_location: `${window.location.origin}${sanitizePathForAnalytics(path)}`,
    page_title: document.title,
  });
}

/**
 * Set user properties for segmentation (e.g., is_hcw).
 */
export function setUserProperties(properties) {
  if (!initialized || !window.gtag) return;
  window.gtag('set', 'user_properties', properties);
}

/**
 * Disable analytics (user revokes consent).
 */
export function disableAnalytics() {
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  initialized = false;
  // Withdrawal covers session replay too — stop recording now.
  import('../instrument').then((m) => m.disableSessionReplay?.()).catch(() => {});
}
